import { OrderManagementPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import PackageSearchIcon from "@redotech/redo-icons/package-search.svg";
import PackageSearchIconFilled from "@redotech/redo-icons/package-search_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoManagedReturnsRpcClientContext } from "@redotech/redo-merchant-managed-returns-app/rpc-client";
import { TaskDomain } from "@redotech/redo-task-common/task";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";
import { useLocation, useMatch } from "react-router-dom";

export const ManagedReturnsItem = memo(function ManagedReturnsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const location = useLocation();
  const managedReturnsRpc = useContext(RedoManagedReturnsRpcClientContext);

  const hasPermissionToViewItem = useHasPermission(
    OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,
  );

  const anyChildSelected = useMatch(`${routes.managedReturnsHref}/*`);

  const showManagedReturns =
    !!team.settings.redoManagedReturns?.enabled && hasPermissionToViewItem;

  const unresolvedTasksLoad = useLoad(
    async (signal) => {
      if (!managedReturnsRpc || !showManagedReturns) {
        return undefined;
      }
      return managedReturnsRpc.getTasksMetrics(
        { domain: TaskDomain.RETURNS },
        { signal },
      );
    },
    [managedReturnsRpc, showManagedReturns, location.pathname],
  );

  const unresolvedTaskCount = unresolvedTasksLoad.value?.pendingTotal;
  const taskNotificationCount =
    unresolvedTaskCount !== undefined && unresolvedTaskCount > 0
      ? unresolvedTaskCount
      : undefined;

  if (!showManagedReturns) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getManagedReturnsSubRoute("inbound-shipments"),
      }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={PackageSearchIconFilled}
      friendlyName="Managed returns"
      id="managed-returns"
      notificationCount={taskNotificationCount}
      UnfilledIcon={PackageSearchIcon}
    >
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getManagedReturnsSubRoute("inbound-shipments"),
        }}
        friendlyName="Inbound shipments"
        id="managed-returns-inbound-shipments"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getManagedReturnsSubRoute("inventory"),
        }}
        friendlyName="Inventory"
        id="managed-returns-inventory"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getManagedReturnsSubRoute("products"),
        }}
        friendlyName="Products"
        id="managed-returns-products"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getManagedReturnsSubRoute("tasks"),
        }}
        friendlyName="Tasks"
        id="managed-returns-tasks"
        notificationCount={taskNotificationCount}
      />
    </RedoSecondaryNavItem>
  );
});
